import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * LeadMagnet — email capture with textured background overlay.
 * Sits directly under the video so the offer feels earned.
 */
export default function LeadMagnet() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [message, setMessage] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please add your first name and email.");
      return;
    }
    setStatus("submitting");
    setMessage("");
    try {
      await axios.post(`${API}/leads`, {
        first_name: firstName.trim(),
        email: email.trim(),
      });
      setStatus("success");
      setMessage(
        "The guide is on the way. Check your inbox (and your promotions folder)."
      );
      toast.success("Sent. Check your inbox.");
      setFirstName("");
      setEmail("");
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setMessage(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
      toast.error("Could not send. Please try again.");
    }
  };

  return (
    <section
      id="guide"
      className="relative overflow-hidden border-t border-[#362b28]"
      data-testid="lead-magnet-section"
    >
      {/* Textured backdrop */}
      <div className="absolute inset-0">
        <img
          src="/brand/reaching-sky.webp"
          alt=""
          aria-hidden
          className="w-full h-full object-cover object-[50%_20%]"
        />
        <div className="absolute inset-0 bg-[#1a1412]/85" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 md:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="eyebrow mb-6"
            >
              Free · 29 Pages
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
              className="font-serif text-[40px] md:text-[68px] leading-[0.98] tracking-tight text-[#e8dcc4]"
            >
              Download Your Free Copy — <br />
              <em className="italic text-[#c28b46]">God&rsquo;s Fingerprint</em>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-8 text-[15px] md:text-[17px] leading-relaxed text-[#c8b99c] max-w-[58ch]"
            >
              A 29-page guide by <em className="text-[#e8dcc4]">Charles H. Washington</em> exploring melanin as biological technology, the science of the <em className="text-[#c28b46]">EarthTone</em> identity spoken of in <em>Genesis 2:7</em>, and the spiritual power you were never taught about.
            </motion.p>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.2 }}
            onSubmit={onSubmit}
            className="col-span-12 md:col-span-5 md:col-start-8 bg-[#231b19]/80 backdrop-blur-md border border-[#362b28] p-8 md:p-10"
            data-testid="lead-magnet-form"
          >
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#c28b46] mb-6">
              → Send the Guide to
            </p>

            <label className="block mb-6">
              <span className="text-[11px] tracking-[0.24em] uppercase text-[#a89b8c]">
                First name
              </span>
              <input
                type="text"
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="e.g. Amari"
                className="input-underline mt-2"
                data-testid="lead-first-name-input"
              />
            </label>

            <label className="block mb-8">
              <span className="text-[11px] tracking-[0.24em] uppercase text-[#a89b8c]">
                Email
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@earth.com"
                className="input-underline mt-2"
                data-testid="lead-email-input"
              />
            </label>

            <button
              type="submit"
              disabled={status === "submitting"}
              className="btn-terra w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="lead-submit-button"
            >
              {status === "submitting" ? "Sending…" : "Send Me The Free Guide"}
              <span aria-hidden>→</span>
            </button>

            {status === "success" && (
              <p
                className="mt-6 text-[13px] leading-relaxed text-[#c28b46]"
                data-testid="lead-success-message"
              >
                {message}
              </p>
            )}
            {status === "error" && (
              <p
                className="mt-6 text-[13px] leading-relaxed text-[#d97757]"
                data-testid="lead-error-message"
              >
                {message}
              </p>
            )}

            <p className="mt-6 text-[11px] leading-relaxed text-[#8a7a6a]">
              No spam. Just the guide, and the occasional word from the movement.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
